import React, { useEffect, useState } from 'react';
import { RadioButton, StatusMessage } from 'is24-corecss';
import { useTranslation } from 'react-i18next';
import { IconS24Close24 } from '@is24/cosma-ui-icons';
import IdentificationInfoContent from '../../components/identificationInfoContent/IdentificationInfoContent';
import './VerificationLandingPage.css';
import VeriffVerification from '../../components/veriffVerification/VeriffVerification';
import { VerificationMethod } from '../../@types/VerificationMethod';
import { useTvsState } from '../../utils/useTvsState';
import { getUserVerified } from '../../api/tvs/userVerifications';
import { VerificationStatus } from '../../@types/VerificationStatus';
import { trackPageView } from '../../tracking/tracking';
import { useClientConfig } from '../../utils/clientConfigContext';
import { ApiResponseError } from '../../api/ApiResponseError';

interface VerificationLandingPageProps {
  shouldShowErrorMessage: boolean;
  onVerificationComplete?: (status: VerificationStatus) => void;
}

const VerificationLandingPage: React.FC<VerificationLandingPageProps> = ({
  shouldShowErrorMessage,
  onVerificationComplete,
}) => {
  const { t } = useTranslation();
  const [modalContent, setModalContent] = useState<React.ReactNode>(null);
  const [, setTvsState] = useTvsState();
  const [shouldShowPage, setShouldShowPage] = useState(shouldShowErrorMessage);
  const [errorMessage, setErrorMessage] = useState('');
  const { clientId } = useClientConfig();
  const isDarkModeOn = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;

  const checkVerificationStatus = () => {
    getUserVerified()
      .then((response) => {
        if (response?.isVerified === true) {
          setTvsState('verification_success');
          onVerificationComplete?.(VerificationStatus.SUCCESS);
        } else {
          setShouldShowPage(true);
        }
      })
      .catch((error: ApiResponseError) => {
        if (error.responseStatus === 401) {
          setErrorMessage(t('errors.unauthorized'));
        }
        setShouldShowPage(true);
      });
  };

  const handleErrorsFromVerificationMethod = (errorMessage: string) => {
    setErrorMessage(t(errorMessage as any));
  };

  useEffect(() => {
    if (!shouldShowErrorMessage) {
      checkVerificationStatus();
      trackPageView('is24.de.sicherheit.verifizierung.modal', clientId);
    } else {
      setErrorMessage(t('VerificationLandingPage.errorMessage'));
    }
  }, []);

  return (
    <>
      {shouldShowPage && (
        <div aria-modal='true' className='modal-backdrop' style={{ zIndex: 2000 }}>
          <div className='modal-container' style={{ width: 601 }}>
            <div className='modal-title-wrapper'>
              <h4 className='modal-headline'>{t('VerificationLandingPage.heading')}</h4>
              <IconS24Close24
                onClick={() => setTvsState('')}
                data-testid='modal-close-icon'
                className='tvs-close-button'
              />
            </div>
            <div className='modal-content-wrapper display-flex'>
              <div className='modal-content palm-display-flex'>
                {errorMessage && (
                  <StatusMessage
                    data-testid='error-message'
                    className='margin-bottom-xxl tvs-status-message'
                    headline={t('VerificationLandingPage.errorHeading')}
                    message={errorMessage}
                    statusType='error'
                  />
                )}
                <div className='grid grid-flex margin-bottom-m'>
                  <IdentificationInfoContent isDarkModeOn={isDarkModeOn} />
                </div>
                <div className='grid-item font-heading-xsmall-bold margin-top-xxl palm-margin-bottom-none'>
                  {t('VerificationLandingPage.availableOptions')}
                </div>
                <div className='grid grid-flex margin-top-xxl gutter-horizontal-m margin-top-m palm-margin-bottom-m palm-margin-top-m'>
                  <div className='grid-item one-whole font-h5'>
                    <RadioButton
                      label='Ausweisdokument'
                      id={VerificationMethod.Veriff}
                      name='verification-method'
                      checked
                      onChange={() => undefined}
                    />
                  </div>
                </div>
                {modalContent}
              </div>
            </div>
            <VeriffVerification
              onContentChange={setModalContent}
              onError={(errorMessage) => handleErrorsFromVerificationMethod(errorMessage)}
              isDarkModeOn={isDarkModeOn}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default VerificationLandingPage;
